import { ListingParamsModel } from './../../models/paginated-response.model';
import { perPage, size } from './../../constants/general.constant';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { IonInfiniteScroll, ModalController } from '@ionic/angular';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { WardrobeService } from './../../services/wardrobe.service';
import { PaginatedResponse } from 'src/app/models/paginated-response.model';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { brand, category, color, type, condition } from 'src/app/constants/general.constant';
import { ProductBrandModel, ProductCategoryModel } from 'src/app/models/wardrobe.model';
import { ProductColorModel, ProductTypeModel, ProductSizeModel } from './../../models/wardrobe.model';

@Component({
  selector: 'shc-product-category-modal',
  templateUrl: './product-category-modal.component.html',
  styleUrls: ['./product-category-modal.component.scss'],
})
export class ProductCategoryModalComponent implements OnInit {
  page: number;
  title: string;
  loading: boolean;
  searchText: string;
  @Input() value: string;
  @Input() typeId: number;
  @Input() categoryId: number;
  @Input() conditions: Array<any>;
  sizes: Array<ProductSizeModel>;
  sizeGroups: Array<{ categoryId: number, labelKey: string, subGroups: Array<{ key: string, labelKey: string, sizes: Array<ProductSizeModel> }> }>;
  expandedGroups: Set<string> = new Set();
  types: PaginatedResponse<ProductTypeModel>;
  brands: PaginatedResponse<ProductBrandModel>;
  colors: PaginatedResponse<ProductColorModel>;
  categories: PaginatedResponse<ProductCategoryModel>;
  params: ListingParamsModel = new ListingParamsModel();
  private searchDebounceTimer: any;
  private brandsRequestId = 0;

  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;

  constructor(
    private wardrobeService: WardrobeService,
    private modalCtrl: ModalController,
    private errorService: ErrorHandlerService
  ) { }

  ngOnInit() {
    this.page = 1;
    this.params.page = 1;
    switch (this.value) {
      case category:
        this.title = marker('Product.Category');
        this.getCategories('');
        break;
      case condition:
        this.title = marker('Product.Condition');
        break;
      case brand:
        this.title = marker('Product.Brand');
        this.getBrands('');
        break;
      case color:
        this.title = marker('Product.Color');
        this.getColors('');
        break;
      case type:
        this.title = marker('Product.Type');
        this.getTypes('');
        break;
      case size:
        this.title = marker('Product.Size');
        this.getSizes();
        break;
      default:
        this.title = marker('Product.Category');
        this.getCategories('');
        break;
    }
  }

  getCategories(event) {
    this.loading = true;
    this.wardrobeService.categoriesList({ page: this.page, per_page: 100 }).subscribe(res => {
      this.loading = false;
      if (this.categories && this.categories.meta) {
        if (this.page === 1) {
          this.categories = res;
        } else {
          this.categories.meta = res.meta;
          this.categories.data = [...this.categories.data, ...res.data];
        }
      } else {
        this.categories = res;
      }
      if (event) {
        event.target.complete();
      }
      this.page++;
    }, error => {
      this.loading = false;
      this.errorService.handle(error);
    });
  }

  getBrands(event) {
    this.loading = true;
    const requestId = ++this.brandsRequestId;

    this.wardrobeService.brandsList(this.params).subscribe(res => {
      if (requestId !== this.brandsRequestId) {
        // Stale response (a newer search/request has since been made) — ignore it
        return;
      }
      this.loading = false;
      if (this.brands && this.brands.meta) {
        if (this.params.page === 1) {
          this.brands = res;
        } else {
            this.brands.meta = res.meta;
            this.brands.data = [...this.brands.data, ...res.data];
        }
      } else {
        this.brands = res;
      }
      if (event) {
        event.target.complete();
      }
      this.page++;
      this.params.page++;
    }, error => {
      this.loading = false;
      this.errorService.handle(error);
    });
  }

  setParams(event) {
    const value = event.target.value;
    this.infiniteScroll.disabled = false;

    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.params.search = value;

      if (!this.params.search) {
        this.params.key = null;
        this.params.page = 1;
        this.getBrands('');
        return;
      }

      this.params.key = 'name';
      this.params.page = 1;
      this.params.per_page = 25;
      this.getBrands('');
    }, 300);
  }

  getColors(event) {
    this.loading = true;
    this.wardrobeService.colorsList({ page: this.page, per_page: perPage }).subscribe(res => {
      this.loading = false;
      if (this.colors && this.colors.meta) {
        if (this.page === 1) {
          this.colors = res;
        } else {
          this.colors.meta = res.meta;
          this.colors.data = [...this.colors.data, ...res.data];
        }
      } else {
        this.colors = res;
      }
      if (event) {
        event.target.complete();
      }
      this.page++;
    }, error => {
      this.loading = false;
      this.errorService.handle(error);
    });
  }

  getTypes(event) {
    this.loading = true;
    this.wardrobeService.typesList({ page: this.page, per_page: perPage }).subscribe(res => {
      this.loading = false;
      if (this.types && this.types.meta) {
        if (this.page === 1) {
          this.types = res;
        } else {
          this.types.meta = res.meta;
          this.types.data = [...this.types.data, ...res.data];
        }
      } else {
        this.types = res;
      }
      if (event) {
        event.target.complete();
      }
      this.page++;
    }, error => {
      this.loading = false;
      this.errorService.handle(error);
    });
  }

  getSizes() {
    this.loading = true;
    let params = { type_id: null, category_id: null };
    if (this.typeId) {
      params.type_id = this.typeId;
    }
    if (this.categoryId) {
      params.category_id = this.categoryId;
    }
    this.wardrobeService.productSizesList(params).subscribe(res => {
      this.loading = false;
      this.sizes = res;
      // "Voor wie" (type) nog niet gekozen: toon uitklapbaar gegroepeerd
      // (Schoenmaten/Kledingmaten, daarbinnen Dames/Heren/Kinderen), i.p.v. platte lijst.
      // category_id 34 = Schoenen, 45 = Kleding. type_id 1=Vrouwen, 2=Mannen, 3=Jongens, 4=Meisjes
      // (Jongens+Meisjes samengevoegd tot "Kinderen", zie categories/types-tabellen).
      if (!this.typeId) {
        this.sizeGroups = this.buildSizeGroups(res);
      } else {
        this.sizeGroups = null;
      }
    }, error => {
      this.loading = false;
      this.errorService.handle(error);
    });
  }

  private buildSizeGroups(res: Array<ProductSizeModel>) {
    const categoryDefs = [
      { categoryId: 34, labelKey: 'Filter.ShoeSizes' },
      { categoryId: 45, labelKey: 'Filter.ClothingSizes' },
    ];
    const typeDefs = [
      { key: 'women', typeIds: [1], shoeLabelKey: 'Filter.WomenShoeSizes', clothingLabelKey: 'Filter.WomenClothingSizes' },
      { key: 'men', typeIds: [2], shoeLabelKey: 'Filter.MenShoeSizes', clothingLabelKey: 'Filter.MenClothingSizes' },
      { key: 'kids', typeIds: [3, 4], shoeLabelKey: 'Filter.KidsShoeSizes', clothingLabelKey: 'Filter.KidsClothingSizes' },
    ];
    const groups = [];
    for (const catDef of categoryDefs) {
      const catSizes = res.filter(s => s.category_id === catDef.categoryId);
      if (!catSizes.length) { continue; }
      const subGroups = [];
      for (const typeDef of typeDefs) {
        const typeSizes = catSizes.filter(s => typeDef.typeIds.includes(s.type_id));
        if (!typeSizes.length) { continue; }
        // Dedupliceren op code: bij "Kinderen" (Jongens+Meisjes samengevoegd) kan
        // dezelfde maat-tekst (bijv. "12") twee keer voorkomen, één rij per geslacht.
        const uniqueTypeSizes = Array.from(
          new Map(typeSizes.map(s => [s.code, s])).values()
        );
        const labelKey = catDef.categoryId === 34 ? typeDef.shoeLabelKey : typeDef.clothingLabelKey;
        subGroups.push({ key: `${catDef.categoryId}_${typeDef.key}`, labelKey, sizes: uniqueTypeSizes });
      }
      if (subGroups.length) {
        groups.push({ categoryId: catDef.categoryId, labelKey: catDef.labelKey, subGroups });
      }
    }
    return groups;
  }

  toggleGroup(key: string) {
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  itemSelected(item) {
    this.modalCtrl.dismiss(item);
  }

  loadData(event): void {
    if (this.value === category) {
      if (this.categories.meta.current_page < this.categories.meta.last_page) {
        this.getCategories(event);
      } else {
        this.infiniteScroll.disabled = true;
      }
    } else if (this.value === brand) {
      if (this.brands.meta.current_page < this.brands.meta.last_page) {
        this.getBrands(event);
      } else {
        this.infiniteScroll.disabled = true;
      }
    } else if (this.value === color) {
      if (this.colors.meta.current_page < this.colors.meta.last_page) {
        this.getColors(event);
      } else {
        this.infiniteScroll.disabled = true;
      }
    } else if (this.value === type) {
      if (this.types.meta.current_page < this.types.meta.last_page) {
        this.getTypes(event);
      } else {
        this.infiniteScroll.disabled = true;
      }
    } else if (this.value === size) {
      this.infiniteScroll.disabled = true;
    }
  }
}
